import { useEffect, useState } from "react";
import { Trash2, Plus, Star, X, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [rev, setRev] = useState({ author: "", rating: 5, text: "", source: "Manuale", date: "" });
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addReview = async () => {
    if (!rev.author || !rev.text) { toast.error("Compila autore e testo"); return; }
    try { await api.post("/admin/reviews", rev); toast.success("Recensione aggiunta"); setRev({ author: "", rating: 5, text: "", source: "Manuale", date: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delReview = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };
  const saveReply = async () => {
    try {
      await api.put(`/admin/reviews/${replyFor.id}/reply`, { reply: replyText });
      toast.success("Risposta salvata"); setReplyFor(null); setReplyText(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="font-display text-4xl text-fir mb-2">Recensioni</h1>
      <p className="text-fir-deep/60 mb-8">Aggiungi recensioni e rispondi pubblicamente agli ospiti.</p>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-stone h-fit">
          <h2 className="font-display text-2xl text-fir mb-4 flex items-center gap-2"><Plus size={18} /> Aggiungi recensione</h2>
          <input data-testid="review-author" placeholder="Autore" value={rev.author} onChange={(e) => setRev({ ...rev, author: e.target.value })} className="input-alpine w-full mb-3" />
          <div className="grid grid-cols-2 gap-3 mb-3">
            <select data-testid="review-rating" value={rev.rating} onChange={(e) => setRev({ ...rev, rating: Number(e.target.value) })} className="input-alpine">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stelle</option>)}
            </select>
            <select value={rev.source} onChange={(e) => setRev({ ...rev, source: e.target.value })} className="input-alpine">
              {["Manuale", "Airbnb", "Booking", "Google"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <input placeholder="Data (es. Feb 2026)" value={rev.date} onChange={(e) => setRev({ ...rev, date: e.target.value })} className="input-alpine w-full mb-3" />
          <textarea data-testid="review-text" placeholder="Testo recensione" value={rev.text} onChange={(e) => setRev({ ...rev, text: e.target.value })} className="input-alpine w-full h-24 mb-3" />
          <button data-testid="add-review-btn" onClick={addReview} className="gold-btn w-full py-2.5 rounded-xl">Aggiungi</button>
        </div>

        <div className="space-y-3">
          {reviews.length === 0 && <p className="text-sm text-fir-deep/50">Nessuna recensione.</p>}
          {reviews.map((r) => (
            <div key={r.id} data-testid="admin-review-row" className="bg-white rounded-2xl p-4 border border-stone">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-fir">{r.author}</span>
                    <span className="flex text-gold">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={12} className="fill-gold" />)}</span>
                    <span className="text-xs text-fir-deep/40">{r.source}</span>
                  </div>
                  <p className="text-sm text-fir-deep/70">{r.text}</p>
                </div>
                <button data-testid="delete-review-btn" onClick={() => delReview(r.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
              </div>
              {r.reply ? (
                <div className="mt-3 pl-3 border-l-2 border-gold bg-ivory rounded-r-lg py-2 px-3">
                  <p className="text-xs uppercase tracking-wide text-wood mb-1">Risposta del proprietario</p>
                  <p className="text-sm text-fir-deep/80">{r.reply}</p>
                  <button data-testid="edit-reply-btn" onClick={() => { setReplyFor(r); setReplyText(r.reply); }} className="text-xs text-fir hover:text-gold mt-1">Modifica risposta</button>
                </div>
              ) : (
                <button data-testid="reply-review-btn" onClick={() => { setReplyFor(r); setReplyText(""); }}
                  className="mt-3 text-sm text-fir hover:text-gold flex items-center gap-1"><MessageSquare size={14} /> Rispondi</button>
              )}
            </div>
          ))}
        </div>
      </div>

      {replyFor && (
        <div className="fixed inset-0 z-50 bg-fir-deep/70 flex items-center justify-center p-4" onClick={() => setReplyFor(null)}>
          <div className="bg-ivory rounded-3xl w-full max-w-lg p-7" onClick={(e) => e.stopPropagation()} data-testid="reply-form">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl text-fir">Rispondi a {replyFor.author}</h2>
              <button onClick={() => setReplyFor(null)}><X className="text-fir" /></button>
            </div>
            <p className="text-sm text-fir-deep/60 italic mb-3">"{replyFor.text}"</p>
            <textarea data-testid="reply-text" value={replyText} onChange={(e) => setReplyText(e.target.value)}
              placeholder="Scrivi la tua risposta pubblica…" className="input-alpine w-full h-28 mb-4" />
            <button data-testid="save-reply-btn" onClick={saveReply} className="gold-btn w-full py-3 rounded-xl">Salva risposta</button>
          </div>
        </div>
      )}
    </div>
  );
}
